import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as payrollService from '../../services/payrollService';
import useUIStore from '../../store/uiStore';
import PayrollTabs from '../../components/PayrollTabs';
import ScrollX from '../../components/ScrollX';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const now = new Date();

// Same job HR already does by hand every month, aimed at the exact
// FACTORY/HO workbook shape (EMP. CODE, Name of Employee, P. days, Month
// days / Total payable/working days, BASIC+DA, HRA, CONVEYANCE, MEDICAL
// ALLOWANCE, Special allowance) - see app/routes/payroll_routes.py's
// _parse_salary_sheet. This module is independent (no existing Employee
// match required): a row whose employee code isn't in Employee Master yet
// gets a brand new profile created from the sheet itself - this is how
// "make a master from the sheet" actually happens. Nothing is saved until
// you press Import.
const SalarySheetImportPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [branch, setBranch] = useState('');
  const [updatePayStructure, setUpdatePayStructure] = useState(false);
  const [report, setReport] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setReport(null);
  };

  const handlePreview = async () => {
    if (!file) {
      showToast('Choose a file first', 'error');
      return;
    }
    setPreviewing(true);
    try {
      const res = await payrollService.previewSheetImport(file);
      if (res.data.status === 'error') {
        showToast(res.data.message, 'error');
        setReport(null);
      } else {
        setReport(res.data);
        showToast(
          `${res.data.create_count} new, ${res.data.update_count} existing, ${res.data.invalid_count} need attention`,
          res.data.invalid_count ? 'info' : 'success'
        );
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to read file - check it is a valid .xlsx workbook', 'error');
      setReport(null);
    }
    setPreviewing(false);
  };

  const importCount = (report?.create_count || 0) + (report?.update_count || 0);

  const handleCommit = async () => {
    if (!file || !importCount) return;
    if (!window.confirm(
      `Import ${report.create_count} new employee(s) and update attendance for ${report.update_count} existing into ${MONTHS[month]} ${year}?` +
      (updatePayStructure ? ' This will also overwrite existing employees’ pay structure from the sheet.' : '')
    )) return;
    setCommitting(true);
    try {
      const res = await payrollService.commitSheetImport(file, month, year, branch, updatePayStructure);
      showToast(`Created ${res.data.created_count} new profile(s), updated ${res.data.updated_count} existing`, 'success');
      navigate('/payroll/employees');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Import failed - re-check the file and try again', 'error');
      setReport(null);
    }
    setCommitting(false);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <PayrollTabs />
        <h2 className="text-lg font-semibold text-white mb-1">Load Old Salary Sheet</h2>
        <p className="text-sm text-white/70 mb-6">
          Upload the same monthly FACTORY/HO workbook HR already prepares (EMP. CODE, Name of Employee,
          P. days, Month days, BASIC+DA, HRA, CONVEYANCE, MEDICAL ALLOWANCE, Special allowance). A row whose
          employee code isn't in Employee Master yet gets a brand new profile created straight from the sheet -
          that's how the master gets built. Nothing is saved until you press Import.
        </p>

        <div className="glass-card p-6 mb-6">
          <div className="flex items-end gap-4 flex-wrap mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select value={month} onChange={(e) => { setMonth(Number(e.target.value)); setReport(null); }} className="border border-gray-300 rounded px-3 py-2 text-sm">
                {MONTHS.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input type="number" value={year} onChange={(e) => { setYear(Number(e.target.value)); setReport(null); }} className="w-24 border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch (for new employees)</label>
              <input value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="FACTORY / HO" className="border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <input type="file" accept=".xlsx,.xlsm" onChange={handleFileChange} className="text-sm" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
            <input type="checkbox" checked={updatePayStructure} onChange={(e) => setUpdatePayStructure(e.target.checked)} />
            Also overwrite existing employees' Basic/HRA/Conveyance/Medical/Special allowance from this sheet
            (off by default - new profiles always get their pay structure from the sheet either way)
          </label>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handlePreview}
              disabled={!file || previewing}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
            >
              {previewing ? 'Reading...' : 'Check File'}
            </button>
            {importCount > 0 && (
              <button
                onClick={handleCommit}
                disabled={committing}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {committing ? 'Importing...' : `Import ${importCount} Employee(s)`}
              </button>
            )}
          </div>
        </div>

        {report && (
          <>
            {report.invalid_count > 0 && (
              <div className="glass-card mb-6">
                <div className="px-5 py-4 border-b border-gray-200">
                  <h2 className="font-semibold text-red-700">
                    {report.invalid_count} row{report.invalid_count === 1 ? '' : 's'} need attention (out of {report.total_rows} in the sheet)
                  </h2>
                  <p className="text-xs text-gray-400 mt-1">These won't be imported - usually a repeated header row in the sheet, or a row missing P. days/Month days (or Name/Basic+DA for a brand new employee).</p>
                </div>
                <ScrollX>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium">Row</th>
                        <th className="px-4 py-2 font-medium">Employee Code</th>
                        <th className="px-4 py-2 font-medium">Name</th>
                        <th className="px-4 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.invalid.map((r) => (
                        <tr key={r.row_number} className="border-b border-gray-50 last:border-0">
                          <td className="px-4 py-2 text-gray-400">{r.row_number}</td>
                          <td className="px-4 py-2">{r.employee_code}</td>
                          <td className="px-4 py-2">{r.name}</td>
                          <td className="px-4 py-2 text-red-600">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollX>
              </div>
            )}

            {report.create_count > 0 && (
              <div className="glass-card mb-6">
                <div className="px-5 py-4 border-b border-gray-200">
                  <h2 className="font-semibold text-green-700">{report.create_count} new employee(s) will be created</h2>
                </div>
                <ScrollX>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium">Employee Code</th>
                        <th className="px-4 py-2 font-medium">Name</th>
                        <th className="px-4 py-2 font-medium text-right">Basic + DA</th>
                        <th className="px-4 py-2 font-medium text-right">P. Days</th>
                        <th className="px-4 py-2 font-medium text-right">Month Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.will_create.map((r) => (
                        <tr key={r.employee_code} className="border-b border-gray-50 last:border-0">
                          <td className="px-4 py-2">{r.employee_code}</td>
                          <td className="px-4 py-2">{r.name}</td>
                          <td className="px-4 py-2 text-right">₹{Number(r.basic_da || 0).toLocaleString('en-IN')}</td>
                          <td className="px-4 py-2 text-right">{r.present_days}</td>
                          <td className="px-4 py-2 text-right">{r.month_days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollX>
              </div>
            )}

            {report.update_count > 0 && (
              <div className="glass-card">
                <div className="px-5 py-4 border-b border-gray-200">
                  <h2 className="font-semibold text-gray-900">{report.update_count} existing employee(s) - attendance will be updated</h2>
                </div>
                <ScrollX>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium">Employee Code</th>
                        <th className="px-4 py-2 font-medium">Name</th>
                        <th className="px-4 py-2 font-medium text-right">P. Days</th>
                        <th className="px-4 py-2 font-medium text-right">Month Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.will_update.map((r) => (
                        <tr key={r.payroll_employee_id} className="border-b border-gray-50 last:border-0">
                          <td className="px-4 py-2">{r.employee_code}</td>
                          <td className="px-4 py-2">{r.name}</td>
                          <td className="px-4 py-2 text-right">{r.present_days}</td>
                          <td className="px-4 py-2 text-right">{r.month_days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollX>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SalarySheetImportPage;
